"use client"

/**
 * @fileoverview MobileBottomNav component with slot architecture
 * @description A composable bottom navigation component optimized for mobile devices
 * @module MobileBottomNav
 * @version 2.0.0 - iOS 26 compatibility update
 */

import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import { cn } from "@/lib/utils"
import {
  type RootProps,
  type NavListProps,
  type NavItemProps,
  type NavContextValue,
  NavVariant,
  NavSize,
  NavItemState,
  LabelPosition,
  IconSize,
  ZIndexLevel,
  NavBorderRadius,
  DEFAULT_ANIMATION_CONFIG,
  DEFAULT_GLASS_CONFIG,
  AnimationType,
} from "./types"
import {
  rootVariants,
  navListVariants,
  navItemVariants,
  iconVariants,
  labelVariants,
  badgeVariants,
  spacerVariants,
} from "./MobileBottomNav.styles"
import { useIOSSafariFix, useIOSFixedReset } from "./useIOSSafariFix"
import "./MobileOptimizations.css"

/* ============================================
   Context
   ============================================ */

/**
 * Navigation context for sharing configuration across components
 */
const NavContext = React.createContext<NavContextValue | null>(null)

/**
 * Hook to access navigation context
 * @throws {Error} When used outside of MobileBottomNav.Root
 * @returns {NavContextValue} Navigation context value
 */
function useNavContext(): NavContextValue {
  const context = React.useContext(NavContext)
  if (!context) {
    throw new Error("MobileBottomNav compound components must be used within MobileBottomNav.Root")
  }
  return context
}

/* ============================================
   Root Component
   ============================================ */

/**
 * Root container for the mobile bottom navigation
 *
 * @component
 * @example
 * \`\`\`tsx
 * <MobileBottomNav.Root variant={NavVariant.GLASS} size={NavSize.MEDIUM}>
 *   <MobileBottomNav.NavList>
 *     {children}
 *   </MobileBottomNav.NavList>
 * </MobileBottomNav.Root>
 * \`\`\`
 *
 * @param {RootProps} props - Component props
 * @returns {React.ReactElement} Navigation root element
 */
const Root = React.forwardRef<HTMLElement, RootProps>(
  (
    {
      children,
      className,
      style,
      variant = NavVariant.GLASS,
      size = NavSize.MEDIUM,
      zIndex = ZIndexLevel.DEFAULT,
      visible = true,
      glassConfig = DEFAULT_GLASS_CONFIG,
      animationConfig = DEFAULT_ANIMATION_CONFIG,
      full = false,
      rounded = NavBorderRadius.NONE,
      gap,
      "aria-label": ariaLabel = "Main navigation",
      "data-testid": testId,
      ...props
    },
    ref,
  ) => {
    const internalRef = React.useRef<HTMLElement>(null)
    const combinedRef = (ref as React.RefObject<HTMLElement>) || internalRef

    const { iosInfo } = useIOSSafariFix({
      enableScrollFix: true,
      enableKeyboardFix: true,
    })

    const { forceReset } = useIOSFixedReset(combinedRef)

    React.useEffect(() => {
      if (visible && iosInfo.hasViewportBugs) {
        // Delay to ensure DOM is ready
        const timer = setTimeout(forceReset, 50)
        return () => clearTimeout(timer)
      }
    }, [visible, iosInfo.hasViewportBugs, forceReset])

    const contextValue = React.useMemo<NavContextValue>(
      () => ({
        variant,
        size,
        animationConfig: { ...DEFAULT_ANIMATION_CONFIG, ...animationConfig },
        gap,
      }),
      [variant, size, animationConfig, gap],
    )

    const customGlassStyles =
      variant === NavVariant.GLASS
        ? {
            "--glass-blur": `${glassConfig.blur ?? DEFAULT_GLASS_CONFIG.blur}px`,
            "--glass-saturation": `${glassConfig.saturation ?? DEFAULT_GLASS_CONFIG.saturation}%`,
            "--glass-opacity": glassConfig.opacity ?? DEFAULT_GLASS_CONFIG.opacity,
            "--glass-border-opacity": glassConfig.borderOpacity ?? DEFAULT_GLASS_CONFIG.borderOpacity,
          }
        : {}

    const roundedValue = typeof rounded === "number" ? undefined : rounded

    // Filter out props that conflict with motion's types
    const { onDrag: _onDrag, ...safeProps } = props as any

    return (
      <NavContext.Provider value={contextValue}>
        <AnimatePresence>
          {visible && (
            <motion.nav
              ref={combinedRef as React.Ref<HTMLElement>}
              className={cn(
                rootVariants({ variant, size, visible, full, rounded: roundedValue }),
                iosInfo.hasViewportBugs && "mobile-nav-root--ios26",
                className,
              )}
              style={
                {
                  zIndex: typeof zIndex === "number" ? zIndex : zIndex,
                  ...(typeof rounded === "number"
                    ? { borderTopLeftRadius: `${rounded}rem`, borderTopRightRadius: `${rounded}rem` }
                    : {}),
                  ...customGlassStyles,
                  ...style,
                } as React.CSSProperties
              }
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 40,
              }}
              role="navigation"
              aria-label={ariaLabel}
              data-testid={testId}
              data-ios-version={iosInfo.majorVersion}
              data-has-viewport-bugs={iosInfo.hasViewportBugs}
              {...(props as any)}
            >
              {children}
            </motion.nav>
          )}
        </AnimatePresence>
      </NavContext.Provider>
    )
  },
)
Root.displayName = "MobileBottomNav.Root"

/* ============================================
   NavList Component
   ============================================ */

/**
 * Container for navigation items
 *
 * @component
 * @example
 * \`\`\`tsx
 * <MobileBottomNav.NavList justify="around">
 *   <MobileBottomNav.NavItem icon={<HomeIcon />} label="Home" />
 * </MobileBottomNav.NavList>
 * \`\`\`
 *
 * @param {NavListProps} props - Component props
 * @returns {React.ReactElement} Navigation list element
 */
const NavList = React.forwardRef<HTMLUListElement, NavListProps>(
  ({ children, className, style, gap, justify = "around", "data-testid": testId, ...props }, ref) => {
    const { size, gap: contextGap } = useNavContext()
    const effectiveGap = gap ?? contextGap

    return (
      <ul
        ref={ref}
        className={cn(navListVariants({ size, justify }), className)}
        style={{
          gap: effectiveGap ? `${effectiveGap}rem` : undefined,
          ...style,
        }}
        role="menubar"
        data-testid={testId}
        {...props}
      >
        {children}
      </ul>
    )
  },
)
NavList.displayName = "MobileBottomNav.NavList"

/* ============================================
   NavItem Component
   ============================================ */

/**
 * Individual navigation item with icon and label
 *
 * @component
 * @example
 * \`\`\`tsx
 * <MobileBottomNav.NavItem
 *   icon={<HomeIcon />}
 *   label="Home"
 *   state={NavItemState.ACTIVE}
 *   onClick={() => navigate('/home')}
 * />
 * \`\`\`
 *
 * @param {NavItemProps} props - Component props
 * @returns {React.ReactElement} Navigation item element
 */
const NavItem = React.forwardRef<HTMLButtonElement, NavItemProps>(
  (
    {
      icon,
      label,
      className,
      style,
      state = NavItemState.INACTIVE,
      labelPosition = LabelPosition.BELOW,
      onClick,
      href,
      iconSize = IconSize.MEDIUM,
      badge,
      "aria-label": ariaLabel,
      "data-testid": testId,
      disabled,
      ...props
    },
    ref,
  ) => {
    const { size, animationConfig } = useNavContext()

    const effectiveState = disabled ? NavItemState.DISABLED : state
    const isDisabled = effectiveState === NavItemState.DISABLED

    const getAnimationProps = () => {
      if (animationConfig.type === AnimationType.NONE) {
        return {}
      }

      return {
        whileTap: isDisabled ? {} : { scale: animationConfig.pressScale ?? 0.95 },
        whileHover: isDisabled ? {} : { scale: animationConfig.hoverScale ?? 1.05 },
        transition:
          animationConfig.type === AnimationType.SPRING
            ? {
                type: "spring",
                stiffness: animationConfig.spring?.stiffness ?? 400,
                damping: animationConfig.spring?.damping ?? 30,
              }
            : {
                type: "tween",
                duration: animationConfig.tween?.duration ?? 0.2,
                ease: animationConfig.tween?.ease ?? "easeOut",
              },
      }
    }

    const content = (
      <>
        <span
          className={cn(iconVariants({ size: iconSize as IconSize }))}
          style={{
            width: typeof iconSize === "number" ? `${iconSize}px` : undefined,
            height: typeof iconSize === "number" ? `${iconSize}px` : undefined,
          }}
        >
          {icon}
          {badge && (
            <span
              className={cn(
                badgeVariants({
                  type: typeof badge === "number" ? "count" : "dot",
                }),
              )}
            >
              {typeof badge === "number" ? badge : null}
            </span>
          )}
        </span>
        <span className={cn(labelVariants({ position: labelPosition, size }))}>{label}</span>
      </>
    )

    const commonProps = {
      className: cn(
        navItemVariants({
          state: effectiveState,
          labelPosition,
          size,
        }),
        className,
      ),
      style,
      "aria-label": ariaLabel ?? label,
      "aria-current": state === NavItemState.ACTIVE ? ("page" as const) : undefined,
      "aria-disabled": isDisabled,
      "data-testid": testId,
      role: "menuitem",
      ...getAnimationProps(),
    }

    // Type-safe props for motion elements
    const motionAnchorProps = {
      ...commonProps,
      href,
      // Omit HTML drag event handlers as they conflict with Motion
      ...(props as any),
      onDrag: undefined,
      onDragStart: undefined,
      onDragEnd: undefined,
    }

    const motionButtonProps = {
      ...commonProps,
      type: "button" as const,
      onClick,
      disabled: isDisabled,
      // Omit HTML drag event handlers as they conflict with Motion
      ...(props as any),
      onDrag: undefined,
      onDragStart: undefined,
      onDragEnd: undefined,
    }

    if (href && !isDisabled) {
      return (
        <li>
          <motion.a
            ref={ref as React.Ref<HTMLAnchorElement>}
            {...motionAnchorProps}
          >
            {content}
          </motion.a>
        </li>
      )
    }

    return (
      <li>
        <motion.button ref={ref} {...motionButtonProps}>
          {content}
        </motion.button>
      </li>
    )
  },
)
NavItem.displayName = "MobileBottomNav.NavItem"

/* ============================================
   Spacer Component
   ============================================ */

/**
 * Spacer component to prevent content from being hidden under navigation
 *
 * @component
 * @example
 * \`\`\`tsx
 * <main>
 *   {content}
 *   <MobileBottomNav.Spacer />
 * </main>
 * \`\`\`
 *
 * @param {object} props - Component props
 * @returns {React.ReactElement} Spacer element
 */
interface SpacerProps {
  /** Additional CSS classes */
  className?: string
  /** Size matching the navigation */
  size?: NavSize
}

const Spacer: React.FC<SpacerProps> = ({ className, size = NavSize.MEDIUM }) => {
  return <div className={cn(spacerVariants({ size }), className)} aria-hidden="true" />
}
Spacer.displayName = "MobileBottomNav.Spacer"

/* ============================================
   Compound Component Export
   ============================================ */

/**
 * MobileBottomNav compound component
 *
 * A fully accessible, mobile-optimized bottom navigation with:
 * - Slot-based architecture (Root, NavList, NavItem)
 * - Glassmorphism visual effects
 * - Motion animations
 * - Safe area support for notched devices
 * - Headless mode for custom styling
 *
 * @example
 * \`\`\`tsx
 * <MobileBottomNav.Root variant={NavVariant.GLASS}>
 *   <MobileBottomNav.NavList>
 *     <MobileBottomNav.NavItem
 *       icon={<Home />}
 *       label="Home"
 *       state={NavItemState.ACTIVE}
 *     />
 *     <MobileBottomNav.NavItem
 *       icon={<Search />}
 *       label="Search"
 *     />
 *   </MobileBottomNav.NavList>
 * </MobileBottomNav.Root>
 * \`\`\`
 */
export const MobileBottomNav = {
  Root,
  NavList,
  NavItem,
  Spacer,
}

export default MobileBottomNav
